import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {format} from 'date-fns';
import {IrpSkippedReportRowDTO} from '../../../../../../../@types/integrated-risk-picture';
import SpringDataPage from '../../../../../../../@types/spring-data-page';
import {useDebounce} from '../../../../../../../hooks/useDebounce';
import {
    useIRPWorkbookStore, usePrimaryFilter, useSkippedReportsPagination,
} from '../../../../../../../stores/irp-workbook.store';
import irpService from '../../../../../../../services/irp.service';

const ALLOWED_SIZES = [10, 25, 50] as const;
type PageSize = typeof ALLOWED_SIZES[number];

/**
 * Skipped Reports Tab
 *
 * Displays paginated NOT_APPLICABLE and MAPPED_INCOMPLETE reports.
 * NOT_APPLICABLE: report was rejected by the LLM as irrelevant to this event sequence.
 * MAPPED_INCOMPLETE: report was mapped to a parent node but the LLM selected no children
 * for one or more OR/XOR gates — requires human review.
 * Reads irpId, primaryFilter, and skippedReportsPagination from Zustand store.
 * No props required.
 */
const SkippedReportsTab: React.FC = () => {
    const irpId = useIRPWorkbookStore((state) => state.irpId);
    const primaryFilter = usePrimaryFilter();
    const pagination = useSkippedReportsPagination();
    const setSkippedReportsPagination = useIRPWorkbookStore((state) => state.setSkippedReportsPagination);
    const navigateToReportAnalysis = useIRPWorkbookStore((state) => state.navigateToReportAnalysis);

    const [rawSearch, setRawSearch] = useState('');
    const debouncedSearch = useDebounce(rawSearch, 400);

    const [rows, setRows] = useState<IrpSkippedReportRowDTO[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    // Incremented by handleRetry to force a re-fetch even when other deps haven't changed
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        setSkippedReportsPagination({ search: debouncedSearch, page: 0 });
    }, [debouncedSearch]);

    useEffect(() => {
        setRawSearch(pagination.search);
    }, [pagination.search]);

    useEffect(() => {
        if (!irpId) return;
        const { dateFrom, dateTo, selectedDescriptors, selectedSources } = primaryFilter;
        const dti = selectedDescriptors.size > 0 ? Array.from(selectedDescriptors) : undefined;
        const sn = selectedSources.size > 0 ? Array.from(selectedSources) : undefined;

        setLoading(true);
        setError(false);
        irpService
            .getWorkbookSkippedReports(
                irpId,
                pagination.page,
                pagination.size,
                dateFrom,
                dateTo,
                dti,
                sn,
                pagination.search || undefined
            )
            .then((page: SpringDataPage<IrpSkippedReportRowDTO>) => {
                setRows(page.content);
                setTotalElements(page.page.totalElements);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [irpId, primaryFilter, pagination.page, pagination.size, pagination.search, retryCount]);

    const handleRetry = (): void => {
        setError(false);
        setRetryCount((c) => c + 1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRawSearch(e.target.value);
    };

    const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        setSkippedReportsPagination({ page: newPage });
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const parsed = parseInt(e.target.value, 10);
        const newSize: PageSize = (ALLOWED_SIZES as readonly number[]).includes(parsed)
            ? (parsed as PageSize)
            : 10;
        setSkippedReportsPagination({ size: newSize, page: 0 });
    };

    const formatDate = (dateTime: string): string => {
        try {
            return format(new Date(dateTime), 'MMM yyyy');
        } catch {
            return dateTime;
        }
    };

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, gap: 2 }}>
                <Typography color="error">Failed to load skipped reports.</Typography>
                <Button variant="outlined" onClick={handleRetry}>Retry</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
            {/* Header bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
                <Typography variant="body2">
                    {totalElements > 0
                        ? `${totalElements} report${totalElements !== 1 ? 's' : ''} require review — either not applicable to this event sequence or mapped with incomplete branch selections.`
                        : ''}
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search explanations..."
                    value={rawSearch}
                    onChange={handleSearchChange}
                    sx={{ width: 240 }}
                />
            </Box>

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            )}

            {/* Empty state */}
            {!loading && totalElements === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <Typography color="text.secondary">
                        No reports require review — all processed reports mapped successfully.
                    </Typography>
                </Box>
            )}

            {/* Table */}
            {!loading && totalElements > 0 && (
                <>
                    <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Source / ID</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Report</TableCell>
                                    <TableCell>AI Analysis</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.reportId} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {row.sourceName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {row.uniqueId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {formatDate(row.dateTime)}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {row.status === 'MAPPED_INCOMPLETE' ? (
                                                <Chip label="Incomplete" color="info" size="small" variant="outlined" />
                                            ) : (
                                                <Chip label="Not Applicable" color="warning" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Tooltip
                                                title={
                                                    <Typography sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                                        {row.content.replace(/\\r\\n|\\n|\\r/g, '\n')}
                                                    </Typography>
                                                }
                                                placement="top"
                                                arrow
                                                enterDelay={500}
                                                leaveDelay={200}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            width: 700,
                                                            maxWidth: 700,
                                                            maxHeight: 500,
                                                            overflow: 'auto',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Typography sx={{
                                                    fontSize: '0.75rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 6,
                                                    WebkitBoxOrient: 'vertical',
                                                    whiteSpace: 'pre-wrap',
                                                    lineHeight: 1.4,
                                                    cursor: 'pointer',
                                                }}>
                                                    {row.excerpt.replace(/\\r\\n|\\n|\\r/g, '\n')}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 360 }}>
                                            {row.explanation != null ? (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        color: row.status === 'MAPPED_INCOMPLETE' ? 'info.dark' : 'warning.dark',
                                                    }}
                                                >
                                                    {row.explanation}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">
                                                    No explanation recorded
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={(): void => navigateToReportAnalysis(row.reportId)}
                                                sx={{ padding: '2px' }}
                                                title="View Report Details"
                                            >
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={pagination.page}
                        rowsPerPage={pagination.size}
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </>
            )}
        </Box>
    );
};

export default SkippedReportsTab;
